import { storeActions, storeMutations } from '@/store/storeConstants'
import store from '@/store'
import { nanoid } from 'nanoid'
import Toaster from '@core/utils/sweetToast'
import { deepSortBySequence } from '@core/utils/filter'
import { filter } from 'mathjs'

const {
  SET_FILES_STATUS,
  SET_FILES_ARRAY,
  SET_UPLOADING_START,
  SET_UPLOADING_PERCENTAGE,
  SET_RESET_NOTIFY,
} = storeMutations

export default () => {
  const fileIds = []
  const fileUpload = async (file, parentId, propsObj) => {
    const fileObject = {
      name: file.name,
      id: nanoid(),
      percentage: 0,
      status: 'uploading',
    }
    try {
      store.commit(`notify/${SET_UPLOADING_START}`, fileObject)

      const config = {
        object: {
          name: file.name,
          type: 1,
          parent_id: parentId || 0,
          ...propsObj,
        },
        component: 'workdrive/',
      }
      const { data } = await store.dispatch(`common/${storeActions.CREATE}`, config)

      const object = {
        component: data.data.url,
        file,
        config: {
          onUploadProgress: progressEvent => {
            const percentage = Math.round((progressEvent.loaded / progressEvent.total) * 100)
            store.commit(`notify/${SET_UPLOADING_PERCENTAGE}`, {
              id: fileObject.id,
              percentage: 5 + Math.round(percentage / 1.052),
            })
            if (percentage === 100) {
              setTimeout(() => {
                store.commit(`notify/${SET_RESET_NOTIFY}`)
              }, 2000)
            }
          },
        },
      }

      // upload to s3
      await store.dispatch(`common/${storeActions.UPDATE_FILE}`, object)
      const payLoad = {
        object: {
          cloud_status: true,
          type: 1,
          ...propsObj,
        },
        component: `workdrive/${data.data.uuid}`,
      }

      // upload to thrid step
      const { data: responeData } = await store.dispatch(`common/${storeActions.UPDATE}`, payLoad)
      store.commit(`notify/${SET_UPLOADING_PERCENTAGE}`, {
        id: fileObject.id,
        percentage: 100,
        status: 'success',
      })
      fileIds.push(data.data.uuid)

      return !!responeData.success
    } catch (error) {
      store.commit(`notify/${SET_UPLOADING_PERCENTAGE}`, {
        id: fileObject.id,
        percentage: 0,
        status: 'failed',
      })
      Toaster.error(error?.response?.data?.message, 'warning')

      return false
    }
  }

  const fileUploader = async (files, parentId, propsObj) => {
    const names = Array.from(files).map(file => {
      return file.name
    })

    store.commit(`notify/${SET_FILES_ARRAY}`, names)
    const promisesToAwait = []

    files.forEach(file => {
      promisesToAwait.push(fileUpload(file, parentId, propsObj))
    })

    await Promise.all(promisesToAwait)

    store.commit(`notify/${SET_FILES_STATUS}`)

    return fileIds
  }

  const fetchAccounts = async params => {
    const payload = {
      component: 'crm/accounts',
      filter: {
        ...params,
      },
    }
    try {
      const { data } = await store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
      if (data.success) {
        return data.data
      }

      return []
    } catch (error) {
      return []
    }
  }

  const fetchLeads = async (params = {}) => {
    const payload = {
      component: 'crm/leads',
      filter: {
        ...params,
      },
    }
    try {
      const { data } = await store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
      if (data.success) {
        return data.data
      }

      return []
    } catch (error) {
      return []
    }
  }

  const fetchOppo = async (params = {}) => {
    const payload = {
      component: 'crm/opportunities',
      filter: {
        ...params,
      },
    }
    try {
      const { data } = await store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
      if (data.success) {
        return data.data
      }

      return []
    } catch (error) {
      return []
    }
  }

  const fetchAllContacts = async (params = {}) => {
    const payload = {
      component: 'setup/contact/list',
      filter: {
        ...params,
      },
    }
    try {
      const { data } = await store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
      if (data.success) {
        return data.data
      }

      return []
    } catch (error) {
      return []
    }
  }

  const fetchModuleContacts = async ({ prop_type, prop_id, search }) => {
    try {
      const payload = {
        component: 'crm/contacts/mappings/list/',
        object: {
          prop_type,
          prop_id,
          search,
        },
      }

      const { data } = await store.dispatch(`common/${storeActions.CREATE}`, payload)
      if (data.success) {
        return data.data
      }

      return []
    } catch (error) {
      return []
    }
  }

  const fetchVendorContact = async ({ opportunityId, search }) => {
    const url = `crm/opportunities/${opportunityId}/vendors`
    const payload = {
      component: url,
      filter: {
        search,
      },
    }
    try {
      const { data } = await store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
      if (data.success) {
        return data.data
      }

      return []
    } catch (error) {
      return []
    }
  }

  const fetchBidder = async ({ id, search }) => {
    const url = `crm/opportunities/${id}/bidders`
    const payload = {
      component: url,
      filter: {
        search,
      },
    }
    try {
      const { data } = await store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
      if (data.success) {
        return data.data
      }

      return []
    } catch (error) {
      return []
    }
  }

  const fetchContacts = async (params = {}) => {
    const payload = {
      component: 'crm/contacts',
      filter: {
        ...params,
      },
    }
    try {
      const { data } = await store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, payload)
      if (data.success) {
        return data.data
      }

      return []
    } catch (error) {
      return []
    }
  }

  const fetchAllFields = async prop_type => {
    const config = {
      component: 'setup/field-entity',
      filter: {
        prop_type,
        limit: -1,
      },
    }

    try {
      const { data } = await store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, config)

      if (data.success) return data.data

      return []
    } catch (error) {
      return []
    }
  }

  const fetchValidationRules = async prop_type => {
    const config = {
      component: 'setup/field-rules',
      filter: {
        prop_type,
        limit: -1,
      },
    }

    try {
      const { data } = await store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, config)

      if (data.success && data.data) return data.data.filter(item => item.is_active)

      return []
    } catch (error) {
      return []
    }
  }

  const fetchPageLayout = async prop_type => {
    try {
      const layoutPayload = {
        component: 'setup/field-layout',
        filter: {
          prop_type,
        },
      }
      const { data } = await store.dispatch(`common/${storeActions.GET_ALL_RECORDS}`, layoutPayload)
      if (data.success) return data.data

      return []
    } catch (error) {
      return []
    }
  }

  // merging all fields with its options
  const mergeGroupOptions = (groups, options) => {
    const groupData = deepSortBySequence(groups || [], 'sequence')
    const optionData = options

    // Group options by field_uuid for faster access
    const optionsMap = optionData.reduce((acc, option) => {
      if (!acc[option.field_uuid]) {
        acc[option.field_uuid] = []
      }

      acc[option.field_uuid].push({
        uuid: option.uuid,
        label: option.label,
        value: option.value,
      })

      return acc
    }, {})

    // Merge options into field_entity
    groupData.forEach(group => {
      group.field_entity.forEach(field => {
        if (optionsMap[field.uuid]) {
          // eslint-disable-next-line no-param-reassign
          field.options = optionsMap[field.uuid]
        }
      })
    })

    return groupData
  }

  // prepare data for sFormulon validate fields
  const validateFields = fields => {
    const inputTypes = {}
    fields.forEach(field => {
      let type = 'string'
      if (field.data_type === 'picklist' || field.data_type === 'multipicklist') type = 'picklist'
      else if (field.data_type === 'number' || field.data_type === 'integer') type = 'number'
      else if (field.data_type === 'boolean' || field.data_type === 'checkbox') type = 'boolean'
      else if (field.data_type === 'date' || field.data_type === 'time') type = 'date'
      inputTypes[field.field_name] = { type }
    })

    return inputTypes
  }

  return {
    fileUpload,
    fileUploader,
    fetchAccounts,
    fetchModuleContacts,
    fetchVendorContact,
    fetchAllContacts,
    fetchOppo,
    fetchBidder,
    fetchLeads,
    fetchContacts,
    mergeGroupOptions,
    validateFields,
    fetchValidationRules,
    fetchAllFields,
    fetchPageLayout,
  }
}
